import React, { useEffect, useLayoutEffect, useState } from 'react';
import { FaPencilAlt, FaRegClock } from 'react-icons/fa';
import { HiTrash } from 'react-icons/hi';
import { useLocation, useNavigate } from 'react-router-dom';
import { AiOutlineClose, AiOutlinePlus } from 'react-icons/ai';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import Button from '../../components/buttons/Button';
import {
  CreateEventModel,
  DateOption,
  EventCustomField,
  EventType,
  Frequency,
} from '../../../types/event';
import IconBadge from '../../components/icons/IconBadge';
import { eventTypeIcons } from '../../../utils/iconUtils';
import {
  CustomFieldType,
  CustomLabelField,
  CustomMultipleChoiceField,
} from '../../../services/model/inviteService.model';
import Labeled from '../../components/misc/Labeled';
import eventService from '../../../services/eventService';
import AutoCompleteLocationInput from '../../components/forms/AutoCompleteLocationInput';
import EventDatesForm, { EventDatesInfo } from '../../components/forms/EventDatesForm';
import ModalLayout from '../../components/layouts/ModalLayout';
import dateUtils from '../../../utils/dateUtils';
import eventUtils from '../../../utils/eventUtils';
import EventNotificationForm from '../../components/forms/EventNotificationForm';
import { AdditionalEventDataMap } from '../../../types/misc';
import DropdownSelect from '../../components/forms/DropdownSelect';
import BusinessImageInput from '../../components/forms/BusinessImageInput';
import Slider from '../../components/misc/Slider';
import { DURATIONS_MS } from '../../../constants';
import { formatUrl, isValidHttpUrl } from '../../../utils/validationUtils';
import useBusinessEffect from '../../../hooks/effects/useBusinessEffect';
import connectionService from '../../../services/connectionService';
import { RootState } from '../../../redux/reducers';
import { Connection } from '../../../redux/slices/connectionsSlice';
import Input from '../../components/misc/Input';
import ExtraOption from '../../components/misc/ExtraOption';
import DatePicker from '../../components/forms/DatePicker';
import StepFlow, { StepFlowStep } from '../../components/misc/StepFlow';
import ParticipantsPicker from '../../components/misc/ParticipantsPicker';
import { formatMoney } from '../../../utils/bankAccountUtil';
import CurrencyInput from '../../components/forms/CurrencyInput';
import { checkOnboarding } from '../../../services/merchant.marketplace.service';
import { CheckOnboardingDto } from '../../../services/model/marketplace.model';

const { ONE_HOUR } = DURATIONS_MS;

export default function CreateEventPage(): JSX.Element {
  const location = useLocation();
  const [step, setStep] = useState<number>(0);
  const [event, setEvent] = useState<Partial<CreateEventModel>>({
    isDatePicker: false,
    ...(location.state?.event ?? {}),
  });

  const handleStep = (diff: 1 | -1) => setStep((prev) => prev + diff);

  const props: ViewProps = { setStep: handleStep, event, setEvent };

  const steps = [
    <ChooseTypeView {...props} />,
    <FillDetailsView {...props} />,
    <ChooseDateView {...props} />,
    <AdditionalDataView {...props} />,
    <ParticipantsView {...props} />,
  ];

  return <StepFlow indexState={[step, setStep]} steps={steps} />;
}

interface ViewProps {
  setStep: (step: 1 | -1) => void;
  event: Partial<CreateEventModel>;
  setEvent: (event: Partial<CreateEventModel>) => void;
}

function ChooseTypeView({ setStep, event, setEvent }: ViewProps) {
  const { t } = useTranslation();

  const setSelected = (type: EventType | undefined) => setEvent({ ...event, type });
  const selected = event.type;

  return (
    <StepFlowStep
      title={t('page.createEvent.type.title')}
      subtitle={t('page.createEvent.type.subtitle')}
      setStep={setStep}
      showPrev={false}
      disabled={!selected}>
      {Object.values(EventType).map((type) => (
        <div
          key={type}
          onClick={() => setSelected(type === selected ? undefined : type)}
          className={classNames(
            'flex w-full cursor-pointer justify-between gap-2 rounded-[8px] border border-secondary-200 px-4 py-2 transition-all',
            { 'bg-secondary-200': selected === type },
          )}>
          <IconBadge icon={eventTypeIcons[type]} className="rounded-[6px] p-1" />
          <div className="flex w-full items-center justify-between">
            <h3 className="text-lg">
              {t(`page.createEvent.type.type.${type.toLowerCase()}.title`)}
            </h3>
            <input type="checkbox" checked={selected === type} />
          </div>
        </div>
      ))}
    </StepFlowStep>
  );
}

function FillDetailsView({ setStep, event, setEvent }: ViewProps) {
  const { t } = useTranslation();

  const { title, description, picture, location, onlineLocation, locationUrl } = event;

  const [errors, setErrors] = useState<Record<string, string>>({});

  useLayoutEffect(() => {
    if (title && description && location && picture) return;
    setEvent({
      ...event,
      title: title ?? '',
      description: description ?? '',
      location: location ?? '',
      picture: picture ?? undefined,
    });
  }, []);

  useEffect(() => {
    setErrors({});
  }, [event]);

  const validate = () => {
    const foundErrors: Record<string, string> = {};

    if (!title) {
      foundErrors.title = t('page.createEvent.fields.title.error');
    }

    if (onlineLocation && locationUrl && !isValidHttpUrl(locationUrl)) {
      foundErrors.locationUrl = t('page.createEvent.fields.location.online.error');
    }

    if (Object.keys(foundErrors).length) {
      setErrors(foundErrors);
      return false;
    }

    return true;
  };

  return (
    <StepFlowStep
      title={t('page.createEvent.info.title')}
      subtitle={t('page.createEvent.info.subtitle')}
      setStep={(diff) => {
        if (diff < 0) {
          setStep(diff);
          return;
        }

        if (!validate()) return;

        if (onlineLocation) {
          setEvent({
            ...event,
            locationUrl: locationUrl ? formatUrl(locationUrl) : undefined,
            location: locationUrl ? formatUrl(locationUrl) : 'Online',
          });
        } else {
          setEvent({ ...event });
        }

        setStep(diff);
      }}
      disabled={Object.keys(errors).length > 0}>
      <Input
        id="title"
        label={t('general.title')}
        type="text"
        value={title}
        onChange={(e) => setEvent({ ...event, title: e.target.value })}
        placeholder={t('page.createEvent.fields.title.placeholder')}
        error={errors.title}
        required
      />
      <Slider
        label={t('general.location')}
        items={[
          { label: t('page.createEvent.fields.location.offline.title'), value: false },
          { label: t('page.createEvent.fields.location.online.title'), value: true },
        ]}
        value={onlineLocation}
        onChange={(isOnline) => {
          setEvent({
            ...event,
            onlineLocation: isOnline,
            location: isOnline ? (event.locationUrl ? formatUrl(event.locationUrl) : 'Online') : '',
            locationUrl: undefined,
          });
        }}
      />
      {onlineLocation ? (
        <Input
          type="text"
          placeholder={t('page.createEvent.fields.location.online.placeholder')}
          value={locationUrl ?? ''}
          onChange={(e) =>
            setEvent({ ...event, locationUrl: e.target.value, location: e.target.value })
          }
          error={errors.locationUrl}
        />
      ) : (
        <AutoCompleteLocationInput
          placeholder={t('page.createEvent.fields.location.offline.placeholder')}
          location={location ?? ''}
          setLocation={({ location: loc, locationUrl: url }) =>
            setEvent({ ...event, location: loc, locationUrl: url })
          }
        />
      )}
      <Input
        id="description"
        label={t(`general.description`)}
        type="textarea"
        className="resize-none"
        placeholder={t('page.createEvent.fields.description.placeholder')}
        value={description}
        onChange={(e) => setEvent({ ...event, description: e.target.value })}
      />
      <Labeled label={t('general.picture')}>
        <BusinessImageInput
          image={picture}
          className="h-[100px] w-[100px]"
          setImage={(image) => setEvent({ ...event, picture: image })}
        />
      </Labeled>
    </StepFlowStep>
  );
}

function ChooseDateView({ setStep, event, setEvent }: ViewProps) {
  const { startTime, isRecurring } = event;
  const { t } = useTranslation();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [open, setOpen] = useState<boolean>(false);
  const dateOptions: Omit<DateOption, 'id' | 'answers'>[] = Array.isArray(event.dateOptions)
    ? event.dateOptions
    : [];

  useLayoutEffect(() => {
    if (!Array.isArray(event.dateOptions)) {
      setEvent({ ...event, dateOptions: [] });
    }
  }, []);

  const handleSetStep = (diff: 1 | -1) => {
    if (!event.isDatePicker) {
      if (diff < 0) {
        setStep(diff);
        return;
      }

      if (!validate()) return;

      if (startTime?.getTime() === event.endTime?.getTime()) {
        setEvent({ ...event, endTime: undefined }); // Unset endTime if it is the same as startTime
      }
    }
    setStep(diff);
  };

  const handleDisabled = () => {
    if (event.isDatePicker) return dateOptions?.length < 2;
    return startTime === undefined;
  };

  const handleOptionChange =
    (index: number) => (option: Omit<DateOption, 'id' | 'answers'> | undefined) => {
      const newOptions = [...dateOptions];
      if (!option) newOptions.splice(index, 1);
      else newOptions[index] = option;
      setEvent({
        ...event,
        dateOptions: eventUtils.sortDateOptions(newOptions as DateOption[], 'time'),
      });
    };

  useLayoutEffect(() => {
    if (startTime && isRecurring) return;

    setEvent({
      ...event,
      isRecurring: isRecurring ?? false,
      ...(!startTime && {
        startTime: dateUtils.roundToNearest5Minutes(new Date(new Date().getTime() + ONE_HOUR)),
      }),
    });
  }, []);

  useEffect(() => {
    setErrors({});
  }, [event]);

  const validate = () => {
    const foundErrors: Record<string, string> = {};

    if (!startTime) {
      foundErrors.startTime = t('page.createEvent.fields.startDate.required');
    }

    if (event.endTime && startTime && event.endTime < startTime) {
      foundErrors.endTime = t('page.createEvent.fields.endDate.invalid');
    }

    if (startTime && startTime < new Date()) {
      foundErrors.startTime = t('page.createEvent.fields.startDate.invalid');
    }

    if (Object.keys(foundErrors).length) {
      setErrors(foundErrors);
      return false;
    }

    return true;
  };

  return (
    <>
      <StepFlowStep
        title={t('page.createEvent.date.title')}
        subtitle={t('page.createEvent.date.subtitle')}
        setStep={handleSetStep}
        disabled={handleDisabled()}
        className="gap-4">
        <Slider
          label={t('page.createEvent.date.options')}
          items={[
            { label: t('general.no'), value: false },
            { label: t('general.yes'), value: true },
          ]}
          value={event.isDatePicker ?? false}
          onChange={(isDatePicker) => setEvent({ ...event, isDatePicker })}
        />

        <Slider
          label={t('page.createEvent.date.attendanceRequired')}
          items={[
            { label: t('general.no'), value: false },
            { label: t('general.yes'), value: true },
          ]}
          value={event.attendanceRequired ?? true}
          onChange={(attendanceRequired) => setEvent({ ...event, attendanceRequired })}
        />

        {!event.isDatePicker ? (
          <EventDatesForm
            info={event as EventDatesInfo}
            setInfo={(info) => setEvent({ ...event, ...info })}
            errors={errors}
          />
        ) : (
          <>
            <h2 className="font-medium">{t('page.createEvent.dateOptions.options')}</h2>
            {dateOptions.map((o, index) => (
              <DateOptionCard key={index} option={o} setOption={handleOptionChange(index)} />
            ))}

            <Button variant="tertiary" onClick={() => setOpen(true)}>
              <AiOutlinePlus className="size-5" />
              {t('page.createEvent.dateOptions.button')}
            </Button>
          </>
        )}
      </StepFlowStep>
      <DateOptionsModal
        open={open}
        type="CREATE"
        setOpen={setOpen}
        setOptions={(options) =>
          setEvent({
            ...event,
            dateOptions: eventUtils.sortDateOptions(
              [...(dateOptions ?? []), ...options] as DateOption[],
              'time',
            ),
          })
        }
      />
    </>
  );
}

interface DateOptionCardProps {
  option: Omit<DateOption, 'id' | 'answers'>;
  setOption: (option: Omit<DateOption, 'id' | 'answers'> | undefined) => void;
}

function DateOptionCard({ option, setOption }: DateOptionCardProps) {
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  return (
    <>
      <div className="flex items-center justify-between rounded-xl border-2 border-secondary-200 border-opacity-50 bg-secondary-200 bg-opacity-50 p-3">
        <div className="flex items-start gap-2">
          <IconBadge icon={FaRegClock} className="mt-0.5 flex-shrink-0 rounded-[6px]" />
          <div className="flex flex-col gap-0.5">
            <p>{eventUtils.formatDate(option)}</p>
            <p className="text-sm text-gray-600">{eventUtils.formatDate(option, 'time')}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setModalOpen(true)}
            className="flex-shrink-0 rounded-full p-3"
            variant="secondary">
            <FaPencilAlt className="h-4 w-4" />
          </Button>
          <Button
            className="flex-shrink-0 rounded-full p-3"
            variant="danger"
            onClick={() => setOption(undefined)}>
            <HiTrash className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <DateOptionsModal
        open={modalOpen}
        type="EDIT"
        setOpen={setModalOpen}
        setOption={setOption}
        option={option}
      />
    </>
  );
}

interface DateOptionsModalCreateProps {
  setOptions: (options: Omit<DateOption, 'id' | 'answers'>[]) => void;
  type: 'CREATE';
}

interface DateOptionsModalEditProps {
  option: Omit<DateOption, 'id' | 'answers'>;
  setOption: (option: Omit<DateOption, 'id' | 'answers'>) => void;
  type: 'EDIT';
}

type DateOptionsModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
} & (DateOptionsModalCreateProps | DateOptionsModalEditProps);

function DateOptionsModal({ open, setOpen, type, ...props }: DateOptionsModalProps) {
  const { option, setOption } = props as DateOptionsModalEditProps;
  const { setOptions } = props as DateOptionsModalCreateProps;

  const { t } = useTranslation();

  const [info, setInfo] = useState<EventDatesInfo | Omit<DateOption, 'id' | 'answers'>>();
  const [view, setView] = useState<'FORM' | 'DATE'>('DATE');

  useLayoutEffect(() => {
    if (!open) return;
    setInfo(type === 'CREATE' ? { isRecurring: false } : option);
  }, [open]);

  const handleCreate = () => {
    if (type !== 'CREATE') throw new Error('Cannot create a date option in edit mode');
    const {
      frequency,
      recurrenceEndDate,
      startTime: startTimeFirst,
      endTime: endTimeFirst,
    } = info as EventDatesInfo;
    if (!startTimeFirst) return;

    const startTimes = dateUtils.getDatesBetween(
      startTimeFirst,
      recurrenceEndDate ?? startTimeFirst,
      frequency ?? Frequency.DAILY,
    );

    const difference = endTimeFirst ? endTimeFirst.getTime() - startTimeFirst.getTime() : undefined;

    const dateOptions = startTimes.map((startTime) => {
      const endTime = difference ? new Date(startTime.getTime() + difference) : undefined;
      return { startTime, endTime };
    });

    setOptions(dateOptions);
    setOpen(false);
  };

  const handleEdit = () => {
    if (type !== 'EDIT') throw new Error('Cannot edit a date option in create mode');
    setOption(info as Omit<DateOption, 'id' | 'answers'>);
    setOpen(false);
  };

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      closeButton
      className={classNames('z-50 mx-6 flex flex-col gap-6 rounded-[20px] bg-secondary-50', {
        'w-full min-w-[380px] max-w-[600px] px-12 py-8': view === 'FORM',
      })}>
      <EventDatesForm
        info={info as unknown as EventDatesInfo}
        setInfo={setInfo}
        includeRecurrence={type === 'CREATE'}
        setView={setView}
        isDatePicker
        includeDeadline={false}
      />
      {view === 'FORM' && (
        <Button
          disabled={!info?.startTime}
          onClick={type === 'CREATE' ? handleCreate : handleEdit}
          variant="primary"
          className="w-full justify-center">
          {t(`component.modal.dateOptions.${type.toLowerCase()}`)}
        </Button>
      )}
    </ModalLayout>
  );
}

function AdditionalDataView({ setStep, event, setEvent }: ViewProps) {
  const { customFields } = event;
  const { t } = useTranslation();

  const [options, setOptions] = useState<AdditionalEventDataMap>({
    poll: !!event.customFields,
    capacity: !!event.maximumAttendees,
    notificationSettings: !!event.notificationSettings,
    deadline: !!event.deadline,
    attendanceRequired: !!event.attendanceRequired,
    priceOfTicket: !!event.priceOfTicket,
  });

  const [onboardStatus, setOnboardStatus] = useState<CheckOnboardingDto | null>(null);

  useEffect(() => {
    checkOnboarding()
      .then(setOnboardStatus)
      .catch(() => setOnboardStatus(null));
  }, []);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const navigate = useNavigate();

  useLayoutEffect(() => {
    if (customFields) return;
    setEvent({ ...event, customFields: customFields ?? undefined });
  }, []);

  useEffect(() => {
    setErrors({});
  }, [event]);

  const validate = () => {
    const foundErrors: Record<string, string> = {};

    if (
      customFields?.some(
        (f) =>
          !f.label || (f.type === CustomFieldType.MULTIPLE_CHOICE && f.options.some((o) => !o)),
      )
    ) {
      foundErrors.customFields = 'true';
    }

    if (event.deadline && options.deadline) {
      if (event.deadline < new Date()) {
        foundErrors.deadline = t('page.createEvent.fields.deadline.invalid');
      }

      if (!event.isDatePicker && event.startTime && event.deadline > event.startTime) {
        foundErrors.deadline = t('page.createEvent.fields.deadline.beforeStartDate');
      }

      if (event.isDatePicker && Array.isArray(event.dateOptions) && event.dateOptions.length > 0) {
        const earliestDateOption = event.dateOptions.sort(
          (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
        )[0];
        if (earliestDateOption && event.deadline > earliestDateOption.startTime) {
          foundErrors.deadline = t('page.createEvent.fields.deadline.beforeStartDate');
        }
      }
    }

    if (Object.keys(foundErrors).length) {
      setErrors(foundErrors);
      return false;
    }

    return true;
  };
  console.log('event', event);
  return (
    <StepFlowStep
      setStep={(diff) => {
        if (diff < 0) {
          setStep(diff);
          return;
        }

        if (!validate()) return;

        setStep(diff);
      }}
      title={t('page.createEvent.additional.title')}
      subtitle={t('page.createEvent.additional.subtitle')}
      disabled={Object.keys(errors).length > 0}>
      <div className="flex flex-col gap-4">
        <EventNotificationForm
          value={event.notificationSettings ?? {}}
          onChange={(settings) => setEvent({ ...event, notificationSettings: settings })}
          isDatePicker={event.isDatePicker ?? false}
        />

        <ExtraOption
          textKey="deadline"
          active={options.deadline ?? false}
          onChange={(a) => {
            const value = Boolean(a);
            setOptions({ ...options, deadline: value });
            setEvent({ ...event, deadline: value ? event.deadline : undefined });
          }}>
          <DatePicker
            className="w-full"
            date={event.deadline}
            minDateTime={dayjs(new Date())}
            minutesStep={5}
            maxDate={
              event.isDatePicker && Array.isArray(event.dateOptions) && event.dateOptions.length > 0
                ? dayjs(event.dateOptions[event.dateOptions.length - 1]?.startTime)
                : undefined
            }
            setDate={(date) => setEvent({ ...event, deadline: date })}
          />
          {errors.deadline && <p className="mt-1 text-sm text-danger">{errors.deadline}</p>}
        </ExtraOption>

        <ExtraOption
          textKey="capacity"
          active={options.capacity}
          onChange={(a) => {
            const value = Boolean(a);
            setOptions({ ...options, capacity: value });
            setEvent({ ...event, maximumAttendees: undefined });
          }}>
          <Input
            type="number"
            min="1"
            value={event.maximumAttendees?.toString().replace(/^0+/, '') || '0'}
            onChange={(e) => {
              if (!Number.isNaN(+e.target.value))
                setEvent({ ...event, maximumAttendees: +e.target.value });
            }}
          />
        </ExtraOption>
        {!event.isDatePicker && (
          <ExtraOption
            textKey="ticketPrice"
            active={!!options.priceOfTicket}
            onChange={(a) => {
              const value = Boolean(a);
              setOptions({ ...options, priceOfTicket: value });
              setEvent({ ...event, priceOfTicket: undefined });
            }}>
            {onboardStatus?.onboarded ? (
              <CurrencyInput
                type="text"
                value={
                  event.priceOfTicket ? `${(event.priceOfTicket / 100).toFixed(2)}` : undefined
                }
                handleChange={(value) => {
                  console.log('value', value);
                  if (!Number.isNaN(value))
                    setEvent({ ...event, priceOfTicket: Math.round(value * 100) });
                }}
              />
            ) : (
              <Button type="button" onClick={() => navigate('/financial/merchantAccount')}>
                {t('page.createEvent.fields.youNeedToOnboardFirst')}
              </Button>
            )}
          </ExtraOption>
        )}

        <ExtraOption
          textKey="poll"
          active={options.poll}
          onChange={(a) => {
            const value = Boolean(a);
            setOptions({ ...options, poll: value });
            setEvent({
              ...event,
              customFields: value ? [eventUtils.EMPTY_CUSTOM_FIELD] : undefined,
            });
          }}>
          <EventPollForm
            fields={customFields ?? [eventUtils.EMPTY_CUSTOM_FIELD]}
            setFields={(fields) => {
              setEvent({ ...event, customFields: fields });
              if (fields.length === 0) setOptions({ ...options, poll: false });
            }}
            showErrors={!!errors.customFields}
          />
        </ExtraOption>
      </div>
    </StepFlowStep>
  );
}

interface EventPollFormProps {
  fields: EventCustomField[];
  setFields: (fields: EventCustomField[]) => void;
  showErrors?: boolean;
}

function EventPollForm({ fields, setFields, showErrors }: EventPollFormProps) {
  const { t } = useTranslation();

  return (
    <div className="flex w-full flex-col gap-6">
      {fields.map((field, index) => (
        <CustomFieldForm
          key={index}
          field={field}
          setField={(f) => {
            const newFields = [...fields];
            if (f) newFields[index] = f;
            if (!f) newFields.splice(index, 1);
            setFields(newFields);
          }}
          showErrors={showErrors}
        />
      ))}
      <Button
        variant="tertiary"
        onClick={() =>
          setFields([
            ...fields,
            {
              label: '',
              mandatory: false,
              options: ['', ''],
              type: CustomFieldType.MULTIPLE_CHOICE,
            } as EventCustomField,
          ])
        }>
        <AiOutlinePlus className="size-5" />
        {t('general.add')}
      </Button>
    </div>
  );
}

interface CustomFieldFormProps {
  field: EventCustomField;
  setField: (field: EventCustomField | undefined) => void;
  showErrors?: boolean;
}

function CustomFieldForm({ field, setField, showErrors }: CustomFieldFormProps) {
  const { t } = useTranslation();

  const getForm = () => {
    switch (field.type) {
      case CustomFieldType.MULTIPLE_CHOICE:
        return (
          <MultipleChoiceForm
            field={field}
            setField={(f) => setField(f as EventCustomField)}
            showErrors={showErrors}
          />
        );
      case CustomFieldType.TEXT:
        return (
          <TextForm
            field={field}
            setField={(f) => setField(f as EventCustomField)}
            showErrors={showErrors}
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2">
        <DropdownSelect
          className="w-full"
          options={[CustomFieldType.TEXT, CustomFieldType.MULTIPLE_CHOICE].map((type) => ({
            value: type,
            label: t(`component.eventPollForm.types.${type}`),
          }))}
          value={field.type}
          setValue={(f) => {
            if (f === field.type) return;
            if (f === CustomFieldType.MULTIPLE_CHOICE)
              setField({
                ...field,
                type: CustomFieldType.MULTIPLE_CHOICE,
                options: ['', ''],
                mandatory: false,
              });
            else
              setField({
                ...field,
                type: CustomFieldType.TEXT,
                options: undefined,
              } as unknown as EventCustomField);
          }}
        />
        <Button
          variant="tertiary"
          className="flex-shrink-0 rounded-full p-3.5"
          onClick={() => setField(undefined)}>
          <HiTrash className="size-5" />
        </Button>
      </div>
      {getForm()}
    </div>
  );
}

interface MultipleChoiceFormProps {
  field: CustomMultipleChoiceField | undefined;
  setField: (field: CustomMultipleChoiceField | undefined) => void;
  showErrors?: boolean;
}

function MultipleChoiceForm({ field, setField, showErrors }: MultipleChoiceFormProps) {
  const { t } = useTranslation();
  if (!field) return null;

  const { options } = field;

  return (
    <div className="flex w-full flex-col gap-4">
      <TextForm
        showErrors={showErrors}
        field={field}
        setField={(f) => setField(f as CustomMultipleChoiceField)}
      />
      <Labeled label={t('component.eventPollForm.options')}>
        <div className="flex flex-col gap-2">
          {options.map((option, index) => (
            <div className="flex items-start gap-2">
              <Input
                placeholder={t('component.eventPollForm.optionPlaceholder', { count: index + 1 })}
                key={index}
                type="text"
                value={option}
                onChange={(e) => {
                  const newOptions = [...options];
                  newOptions[index] = e.target.value;
                  setField({ ...field, options: newOptions });
                }}
                {...(showErrors && {
                  error: !option ? t('component.eventPollForm.errors.emptyOption') : undefined,
                })}
                appendButton={
                  <Button
                    className="flex-shrink-0 rounded-full p-3.5"
                    disabled={options.length <= 2}
                    onClick={() =>
                      setField({ ...field, options: options.filter((_, i) => i !== index) })
                    }>
                    <AiOutlineClose className="h-4 w-4" />
                  </Button>
                }
              />
            </div>
          ))}
        </div>
      </Labeled>
      <Button
        className="p-0 underline"
        onClick={() => {
          setField({ ...field, options: [...options, ''] });
        }}>
        {t('component.eventPollForm.addOption')}
      </Button>
    </div>
  );
}

interface TextFormProps {
  field: CustomLabelField;
  setField: (field: CustomLabelField | undefined) => void;
  showErrors?: boolean;
}

function TextForm({ field, setField, showErrors }: TextFormProps) {
  const { t } = useTranslation();
  const { label, mandatory } = field;
  return (
    <>
      <Labeled label={t('component.eventPollForm.question.title')}>
        <div className="flex gap-2">
          <Input
            placeholder={t('component.eventPollForm.question.placeholder')}
            type="text"
            value={label}
            onChange={(e) => setField({ ...field, label: e.target.value })}
            {...(showErrors && {
              error: !label ? t('component.eventPollForm.errors.emptyQuestion') : undefined,
            })}
          />
          <div className="w-[46px]" />
        </div>
      </Labeled>
      <Labeled label={`${t('general.mandatory')}?`}>
        <Slider
          items={[
            { label: t('general.no'), value: false },
            { label: t('general.yes'), value: true },
          ]}
          value={mandatory}
          onChange={(value) => setField({ ...field, mandatory: value })}
        />
      </Labeled>
    </>
  );
}

export const inviteButtonKey = 'inviteButton';

function ParticipantsView({ setStep, event, setEvent }: ViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { participantsIds } = event;

  const connections = useSelector((state: RootState) => state.connections).filter(
    (c) => c.userId,
  ) as Connection[];

  const activeConnections = connections.filter((c) => c.active !== false);

  useLayoutEffect(() => {
    if (participantsIds && participantsIds.length > 0) return;
    setEvent({ ...event, participantsIds: activeConnections.map((c) => +c.userId!) });
  }, []);

  useBusinessEffect(() => {
    connectionService.getConnections();
  }, []);

  const handleSubmit = () => {
    const toInvite = participantsIds ?? [];
    const inviteAll = activeConnections.length === toInvite.length;

    eventService
      .createEvent(event as CreateEventModel, toInvite, inviteAll)
      .then(() => navigate('/events'));
  };

  const participants = connections.filter((c) => participantsIds?.includes(+c.userId!));

  return (
    <StepFlowStep
      setStep={setStep}
      title={t('page.createEvent.participants.title')}
      subtitle={t('page.createEvent.participants.subtitle')}
      showNext={false}
      onSubmit={handleSubmit}>
      <ParticipantsPicker
        selected={participants}
        disable={(c) => !c.userId}
        setSelected={(ids) => {
          setEvent({
            ...event,
            participantsIds: connections
              .filter((c) => ids.map((id) => id.id).includes(c.id!))
              .map((c) => +c.userId!),
          });
        }}
      />
    </StepFlowStep>
  );
}
